/**
 * Error classes for ProseMirror replay operations.
 *
 * When replaying ProseMirror steps, operations can fail if the document
 * structure doesn't match expectations. These errors provide detailed
 * debugging information for diagnosing and recovering from failures.
 */

/**
 * Error thrown when a ProseMirror step fails to apply.
 *
 * Unlike plain text replay (which can always splice strings), ProseMirror
 * steps can fail if the document structure doesn't match expectations.
 * This is a fatal error - we cannot continue replay after a step failure.
 *
 * @example
 * ```typescript
 * try {
 *   const state = builder.getStateAtIndex(100);
 * } catch (error) {
 *   if (error instanceof StepApplicationError) {
 *     console.error(`Replay failed at event ${error.eventIndex}, step ${error.stepIndex}`);
 *     console.error(`Failure: ${error.stepFailure}`);
 *     // Fall back to plain text replay
 *   }
 * }
 * ```
 */
export class StepApplicationError extends Error {
  /** Event index where the failure occurred */
  readonly eventIndex: number;

  /** Step index within the event's prosemirrorSteps array */
  readonly stepIndex: number;

  /** ProseMirror's failure message (from StepResult.failed) */
  readonly stepFailure: string;

  /** Current document state at time of failure (JSON for debugging) */
  readonly documentStateJSON: Record<string, unknown>;

  /** The step that failed to apply (JSON) */
  readonly failedStepJSON: Record<string, unknown>;

  constructor(params: {
    eventIndex: number;
    stepIndex: number;
    stepFailure: string;
    documentStateJSON: Record<string, unknown>;
    failedStepJSON: Record<string, unknown>;
  }) {
    super(
      `Step application failed at event ${params.eventIndex}, step ${params.stepIndex}: ${params.stepFailure}`
    );
    this.name = "StepApplicationError";
    this.eventIndex = params.eventIndex;
    this.stepIndex = params.stepIndex;
    this.stepFailure = params.stepFailure;
    this.documentStateJSON = params.documentStateJSON;
    this.failedStepJSON = params.failedStepJSON;
  }
}
